import { Download, Loader2, Share, Smartphone, SquarePlus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import {
  clearDeferredPwaInstallPrompt,
  getDeferredPwaInstallPrompt,
  subscribeToPwaInstallPrompt,
} from 'src/pwa/install-prompt';
import { isIosSafari, isPwaStandalone, notifyPwaInstallAccepted, PWA_INSTALL_ACCEPTED_EVENT } from 'src/pwa/pwa';
import { pushRepeatableDataLayerEvent } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';

const DashboardPwaInstallCard: React.FC = () => {
  const t = useTranslationByKey('PAGES.HOME.PWA_INSTALL');
  const [deferredPrompt, setDeferredPrompt] = useState(getDeferredPwaInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(isPwaStandalone);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showIosGuidance, setShowIosGuidance] = useState(false);
  const displayTrackedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = subscribeToPwaInstallPrompt(setDeferredPrompt);
    const markInstalled = () => {
      setIsInstalled(true);
      setIsInstalling(false);
      setShowIosGuidance(false);
      setDeferredPrompt(null);
      clearDeferredPwaInstallPrompt();
    };

    window.addEventListener('appinstalled', markInstalled);
    window.addEventListener(PWA_INSTALL_ACCEPTED_EVENT, markInstalled);

    return () => {
      unsubscribe();
      window.removeEventListener('appinstalled', markInstalled);
      window.removeEventListener(PWA_INSTALL_ACCEPTED_EVENT, markInstalled);
    };
  }, []);

  const canOfferInstall = !isInstalled && (deferredPrompt !== null || isIosSafari());

  useEffect(() => {
    if (!canOfferInstall || displayTrackedRef.current) {
      return;
    }

    displayTrackedRef.current = true;
    pushRepeatableDataLayerEvent(GtmEventName.PwaInstallCtaDisplayed, {
      eligibility_reason: 'persistent_dashboard_option',
      placement: 'dashboard_install_card',
      platform: deferredPrompt ? 'beforeinstallprompt' : 'ios_safari',
    });
  }, [canOfferInstall, deferredPrompt]);

  const install = async () => {
    if (!deferredPrompt) {
      setShowIosGuidance(true);
      pushRepeatableDataLayerEvent(GtmEventName.PwaIosGuidanceDisplayed, {
        placement: 'dashboard_install_card',
      });
      return;
    }

    setIsInstalling(true);

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      clearDeferredPwaInstallPrompt();
      setDeferredPrompt(null);

      pushRepeatableDataLayerEvent(
        choice.outcome === 'accepted' ? GtmEventName.PwaInstallAccepted : GtmEventName.PwaInstallDismissed,
        {
          placement: 'dashboard_install_card',
          platform: choice.platform || 'beforeinstallprompt',
          stage: 'native_prompt',
        }
      );

      if (choice.outcome === 'accepted') {
        notifyPwaInstallAccepted();
      }
    } catch (error) {
      console.error('Failed to open the PWA install prompt:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  if (!canOfferInstall) {
    return null;
  }

  const copy = {
    closeGuidance: t('CLOSE_GUIDANCE', { defaultValue: 'Close install instructions' }),
    install: t('INSTALL', { defaultValue: 'Install app' }),
    installing: t('INSTALLING', { defaultValue: 'Opening installer' }),
    iosTitle: t('IOS_TITLE', { defaultValue: 'Install RewardBloom on your iPhone or iPad' }),
    iosStepOne: t('IOS_STEP_ONE', { defaultValue: 'Tap the Share button in Safari.' }),
    iosStepTwo: t('IOS_STEP_TWO', { defaultValue: 'Choose “Add to Home Screen”.' }),
    subtitle: t('SUBTITLE', {
      defaultValue: 'Add RewardBloom to your device for quicker access to your dashboard and progress.',
    }),
    title: t('TITLE', { defaultValue: 'Keep RewardBloom one tap away' }),
  };

  return (
    <section
      aria-labelledby="dashboard-pwa-install-title"
      className="w-full overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-sm"
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center md:p-5">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 ring-1 ring-violet-200">
            <Smartphone className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 id="dashboard-pwa-install-title" className="font-manrope text-base font-extrabold text-slate-900">
              {copy.title}
            </h2>
            <p className="mt-1 font-manrope text-sm leading-5 text-slate-600">{copy.subtitle}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => void install()}
          disabled={isInstalling}
          aria-controls={isIosSafari() ? 'dashboard-pwa-ios-guidance' : undefined}
          aria-expanded={isIosSafari() ? showIosGuidance : undefined}
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-violet-700 px-5 py-2.5 font-manrope text-sm font-extrabold text-white shadow-sm transition hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
        >
          {isInstalling ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {isInstalling ? copy.installing : copy.install}
        </button>
      </div>

      {showIosGuidance && (
        <div
          id="dashboard-pwa-ios-guidance"
          role="status"
          className="border-t border-violet-100 bg-white/80 px-4 py-4 md:px-5"
        >
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-manrope text-sm font-bold text-slate-900">{copy.iosTitle}</p>
              <ol className="mt-3 space-y-2 font-manrope text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <Share className="h-4 w-4 shrink-0 text-violet-700" aria-hidden="true" />
                  <span>{copy.iosStepOne}</span>
                </li>
                <li className="flex items-center gap-2">
                  <SquarePlus className="h-4 w-4 shrink-0 text-violet-700" aria-hidden="true" />
                  <span>{copy.iosStepTwo}</span>
                </li>
              </ol>
            </div>
            <button
              type="button"
              onClick={() => setShowIosGuidance(false)}
              aria-label={copy.closeGuidance}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default DashboardPwaInstallCard;
